import { useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { Pipeline, PipelineInput } from '@ui/__generated__/graphql'
import { Button, ToggleSwitch } from '@ui/components/core'
import { showToast } from '@ui/components/elements/ToastManager/ToastManager'
import { useSavePipeline } from '@ui/lib/hooks/useSavePipeline'
import { removeTypename } from '@ui/lib/utils'
import { UpdatePipelineMutation } from '@ui/loaders/workflow'
import { SaveModal } from '@ui/pages/pipelinesWorkflow/components/SaveUpdateEnableLoaf/SaveModal'
import { useAuth } from '@ui/providers/AuthProvider.tsx'
import { useNodeAndEdgeStore, usePipelinesStore, useSaveUpdateStatusStore } from '@ui/stores'

export function ToggleEnableLoaf() {
  const location = useLocation()

  const { isDemoMode } = useAuth()

  const { updateWorkflow } = useSavePipeline()
  const [updatePipeline] = UpdatePipelineMutation()

  const { selectedPipeline: pipeline, selectPipeline } = usePipelinesStore(
    useShallow((state) => ({
      selectedPipeline: state.selectedPipeline,
      selectPipeline: state.selectPipeline,
    }))
  )

  const { loafId, loafState, setLoafState, loafName, nodes, hasBeenModified } = useNodeAndEdgeStore(
    useShallow((state) => ({
      loafId: state.loafId,
      loafState: state.loafState,
      setLoafState: state.setLoafState,
      loafName: state.loafName,
      nodes: state.nodes,
      hasBeenModified: state.getHasBeenModified,
    }))
  )

  const { pipelineModified, setSaveButtonEnabled, saveButtonEnabled } = useSaveUpdateStatusStore(
    useShallow((state) => ({
      pipelineModified: state.pipelineModified,
      setSaveButtonEnabled: state.setSaveButtonEnabled,
      saveButtonEnabled: state.saveButtonEnabled,
    }))
  )

  const [open, setOpen] = useState(false)

  const isNewWorkflow = location.pathname.includes('pipelines/new')

  const savePipeline = async () => {
    setOpen(true)
  }

  const onClick = async (pipeline: Pipeline | undefined, enabled: boolean) => {
    if (isDemoMode) return
    if (loafName == undefined) {
      setOpen(true)
    } else {
      if (!pipeline) return

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { nodes, edges, ...pipelineRest } = pipeline

      let updateResult
      try {
        updateResult = await updatePipeline({
          variables: {
            pipeline: {
              ...removeTypename(pipelineRest),
              enabled,
            },
          },
        })
      } catch (error) {
        showToast.error(`Failed to ${enabled ? 'enable' : 'disable'} pipeline ${pipeline.name}`)
      }

      if (updateResult?.data?.updatePipeline) {
        selectPipeline({ ...pipeline, enabled: updateResult.data.updatePipeline.enabled })
        setLoafState(updateResult.data.updatePipeline.enabled)
      }
    }
  }

  useEffect(() => {
    const twoNodes = nodes.filter((node) => node.type != 'placeholder').length >= 2
    const enabled = !isDemoMode && ((twoNodes && pipelineModified) || (twoNodes && !loafId))

    if (isNewWorkflow && !isDemoMode) {
      setSaveButtonEnabled(true)
      return
    }

    setSaveButtonEnabled(enabled)
  }, [setSaveButtonEnabled, hasBeenModified, loafId, nodes, pipelineModified, isNewWorkflow])

  return (
    <div className="group flex gap-x-4">
      <SaveModal open={open} setOpen={setOpen} pipeline={pipeline as PipelineInput} />
      <ToggleSwitch label={'Enable'} checked={loafState} onChange={() => onClick(pipeline!, !loafState)} />
      <Button
        disabled={!saveButtonEnabled}
        onClick={() => {
          !isNewWorkflow
            ? updateWorkflow({ pipeline: pipeline as PipelineInput, isNew: isNewWorkflow })
            : savePipeline()

          setSaveButtonEnabled(false)
        }}>
        {!isNewWorkflow ? 'Update' : 'Save'} pipeline
      </Button>
    </div>
  )
}
